import { type ExcludeValues, isNonArrayObject, type ReadonlyRecord } from '../../utils'

import type { CommissioningFileParsingError, ConnectwareError, ConnectwareServiceCreationError, CybusService } from '..'
import { isLoadableLoaded, type Loadable } from '../Loadable'

export type CybusServiceSchema = ReadonlyRecord<string, unknown>

/**
 * Only supports primitive types or primitive type arrays
 */
export type CybusServiceParameterValue = string | number | boolean | readonly (string | number)[]

export type CybusServiceParameters = ReadonlyRecord<string, CybusServiceParameterValue>

/**
 * Parameters as they can be emitted live by the parameters form (RJSF), before being resolved
 * into `CybusServiceParameters` for storage/dispatch. Unlike `CybusServiceParameters`, a value
 * here can be `undefined` — the secret widget's onChange (and RJSF itself, when a field is
 * cleared) emits `undefined` to mean "no value typed", not "an empty value"
 */
export type CybusServiceRawParameters = ReadonlyRecord<string, CybusServiceParameterValue | undefined>

export type CybusServiceForm = Readonly<{
    creating: boolean

    /**
     * The file can be in three states
     *
     * A string of its contents, a non loaded null value or a loaded `ConnectwareError`
     */
    file: Loadable<string>

    /**
     * The schema of the service to be created
     *
     * A Record of the schema, a non loaded null value or a loaded `ConnectwareError`
     */
    schema: Loadable<CybusServiceSchema>

    /**
     * The id of the given service
     *
     * It is null only if the file has not been parsed yet
     */
    id: CybusService['id'] | null

    /**
     * The service creation parameters
     */
    parameters: CybusServiceParameters | null

    /**
     * Where the requesting process is controlled
     */
    requesting: CommissioningFileParsingError | ConnectwareServiceCreationError | ConnectwareError | boolean

    /**
     * The initial value of the form
     */
    initialFormValue: ExcludeValues<Pick<CybusServiceForm, 'file' | 'id' | 'parameters'>, null> | null
}>

export const isAddOrUpdateServiceSchemaValid = (schema: CybusServiceForm['schema']): schema is CybusServiceSchema => isLoadableLoaded(schema)

export const isAddOrUpdateServiceCommissioningFileValid = (file: CybusServiceForm['file']): file is string => isLoadableLoaded(file)

export const isSecretProperty = (property: unknown): property is Readonly<{ secret: true }> => isNonArrayObject(property) && property.secret === true

/**
 * A `secret: true` property declared `type: 'string'` — the only shape the write-only masking
 * widget (`SecretParameterWidget`) can render, and the only shape the ticket's own spec allows a
 * secret parameter definition to have (service-manager's `baseSchema.yml` only lets a string
 * parameter definition declare `secret: true`). A secret property of any other type (e.g. array)
 * still has its value stripped like any other secret (see `getSecretParameterNames`); it just falls
 * through to rjsf's normal, unmasked rendering for its type instead of being assigned the widget.
 */
const isMaskableSecretProperty = (property: unknown): property is Readonly<{ secret: true, type: 'string' }> =>
    isNonArrayObject(property) && property.secret === true && property.type === 'string'

/**
 * Loose schema shape accepted by the parameter-name lookups below: only `properties` is read, so
 * both `CybusServiceSchema` and a raw `JSONSchema7` (which lacks a string index signature) satisfy
 * it without a cast at the call site
 */
type SchemaWithProperties = Readonly<{ properties?: unknown }>

const getParameterNamesMatching = (schema: SchemaWithProperties, matches: (property: unknown) => boolean): readonly string[] => {
    const { properties } = schema

    if (!isNonArrayObject(properties)) {
        return []
    }

    return Object.entries(properties)
        .filter(([, property]) => matches(property))
        .map(([name]) => name)
}

/**
 * Names of the parameters flagged `secret: true` in the parameters schema —
 * their values are write-only: the API never renders them back
 */
export const getSecretParameterNames = (schema: SchemaWithProperties): readonly string[] => getParameterNamesMatching(schema, isSecretProperty)

/**
 * Names of the `secret: true` parameters that `getSecretParameterNames` also assigns the masking
 * widget to — see `isMaskableSecretProperty`
 */
export const getMaskableSecretParameterNames = (schema: SchemaWithProperties): readonly string[] => getParameterNamesMatching(schema, isMaskableSecretProperty)
